import React from 'react';
import _ from 'lodash';
import styles from '../styles/components/ActionButton.module.css';
import { MoonLoader } from 'react-spinners';

export interface ActionButtonIcon {
  source?: string;
  size?: 'xsmall' | 'small' | 'normal' | 'xnormal' | 'large';
  position?: 'top' | 'left' | 'right';
}

export type ButtonShadow = 'none' | '1' | '2' | '3' | '3_5' | '4';

export interface ActionButtonProps {
  text?: string;
  type?:
    | 'primary'
    | 'primaryAlt'
    | 'secondary'
    | 'secondaryAlt'
    | 'link'
    | 'icon';
  size?: 'small' | 'normal' | 'large';
  icon?: ActionButtonIcon;
  disabled?: boolean;
  shadowNormal?: ButtonShadow; // This will override the effect of 'shadowed'
  shadowHover?: ButtonShadow; // This will override the effect of 'shadowed'
  shadowDisabled?: ButtonShadow;
  onClick: () => void;
  loading?: boolean;
  tabIndex?: number;
}

const loadingIconSizes: { [key: string]: number } = {
  small: 12,
  normal: 16,
  large: 20,
};

export const ActionButton: React.FC<ActionButtonProps> = ({
  text,
  type = 'primary',
  size = 'normal',
  icon = {},
  disabled = false,
  shadowNormal,
  shadowHover,
  shadowDisabled,
  onClick,
  loading = false,
  tabIndex,
}) => {
  const isIconOnlyButton = text === undefined;
  const _shadowNormal = shadowNormal || (isIconOnlyButton ? 'none' : '2');
  const _shadowHover = shadowHover || (isIconOnlyButton ? '4' : '4');
  const _shadowDisabled = shadowDisabled || (isIconOnlyButton ? 'none' : '1');
  const _icon = { size: 'normal', position: 'left', ...icon };
  const img = _icon.source && (
    <img
      className={[
        styles[type + 'Icon'],
        styles[_icon.size + 'Icon'],
        styles[_icon.position + 'Icon'],
      ].join(' ')}
      src={_icon.source}
      alt="action button"
    />
  );
  return (
    <div
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
        e.key === 'Enter' && onClick();
      }}
      className={[
        styles[type + 'Container'],
        styles[size],
        disabled ? styles.disabled : '',
        _icon.position === 'top' ? styles.vertical : styles.horizontal,
        _shadowNormal === 'none' ? '' : styles['shadowNormal' + _shadowNormal],
        _shadowHover === 'none' ? '' : styles['shadowHover' + _shadowHover],
        _shadowDisabled === 'none'
          ? ''
          : styles['shadowDisabled' + _shadowDisabled],
      ].join(' ')}
    >
      {!loading && _icon.position !== 'right' && img}
      {loading && _icon.position !== 'right' && (
        <div className={styles.leftLoadingIcon}>
          <MoonLoader
            color={_.startsWith(type, 'primary') ? '#FFFFFF' : '#002F6C'}
            size={loadingIconSizes[size]}
          />
        </div>
      )}
      {text && <span className={styles[type + 'Text']}>{text}</span>}
      {!loading && _icon.position === 'right' && img}
      {loading && _icon.position === 'right' && (
        <div className={styles.rightLoadingIcon}>
          <MoonLoader
            color={_.startsWith(type, 'primary') ? '#FFFFFF' : '#002F6C'}
            size={loadingIconSizes[size]}
          />
        </div>
      )}
    </div>
  );
};
